'use strict';

const utils = require('public/js/helpers/utils');
const localStorageSlim = require('localstorage-slim');

const GARAGE_STORAGE_KEY = 'rp:garage:v1';
const GARAGE_TTL_SECONDS = 60 * 30;
const DISABLE_CONTEXTUAL_LOADING_KEY = 'disable_contextual_loading';
const RP_GARAGE_STATE_KEY = '__rp_vehicle_picker_garage_state_v1';

function isContextualLoadingDisabled() {
	return localStorageSlim.get(DISABLE_CONTEXTUAL_LOADING_KEY) === '1';
}

function validateGaragePayload(garage) {
	return Array.isArray(garage);
}

/**
 * Garage data
 * @section garage
 */

const createDefaultGarageState = () => {
	return { garage: null, loaded: false, loading: false, cacheCleared: false };
};

const getGarageState = () => {
	if (typeof globalThis === 'undefined') {
		return createDefaultGarageState();
	}

	if (!globalThis[RP_GARAGE_STATE_KEY]) {
		globalThis[RP_GARAGE_STATE_KEY] = createDefaultGarageState();
	}

	return globalThis[RP_GARAGE_STATE_KEY];
};

function triggerGarageUpdated() {
	const state = getGarageState();
	if (typeof document === 'undefined' || typeof rp$ !== 'function') {
		return;
	}
	const $doc = rp$(document);
	if (!$doc || typeof $doc.trigger !== 'function') {
		return;
	}
	$doc.trigger('garageUpdated', state.garage);
}

function loadGarageFromCache(clearCached = false) {
	const state = getGarageState();

	if (isContextualLoadingDisabled()) {
		return false;
	}

	if (clearCached === true && state.cacheCleared !== true) {
		localStorageSlim.remove(GARAGE_STORAGE_KEY);
		state.cacheCleared = true;
		return false;
	}

	const storedGarage = localStorageSlim.get(GARAGE_STORAGE_KEY);
	if (!validateGaragePayload(storedGarage)) {
		return false;
	}
	state.garage = storedGarage;
	state.loaded = true;
	state.loading = false;

	triggerGarageUpdated();
	return true;
}

function loadUserGarage() {
	if (isContextualLoadingDisabled()) {
		return;
	}

	const state = getGarageState();

	if (state.loading) {
		return;
	}

	// Defensive programming: do not run this for bots as they are stateless and will not use the local storage cache.
	// This will prevent a large number of unnecessary API calls as every page load will not make this call
	if (utils.isBot()) {
		state.garage = [];
		state.loaded = true;
		state.loading = false;
		triggerGarageUpdated();
		
		return;
	}

	state.loading = true;

	globalThis.$ajax({
		method: 'GET',
		url: '/ajax/vehicle-picker/get-garage',
		dataType: 'json',
		success: (response) => {
			const garage = response?.garage ?? [];
			state.garage = validateGaragePayload(garage) ? garage : [];
			state.loaded = true;
			state.loading = false;

			localStorageSlim.set(GARAGE_STORAGE_KEY, state.garage, { ttl: GARAGE_TTL_SECONDS });
			triggerGarageUpdated();
		},
		error: () => {
			state.garage = [];
			state.loaded = true;
			state.loading = false;

			triggerGarageUpdated();
		},
	});
}

function clearGarageCache() {
	localStorageSlim.remove(GARAGE_STORAGE_KEY);
	const state = getGarageState();
	state.garage = null;
	state.loaded = false;
	state.loading = false;
}

function getGarageData(isVehicleSpecificPage = false) {
	const state = getGarageState();

	if (isContextualLoadingDisabled() || state.loading) {
		return null;
	}

	if (state.garage) {
		return state.garage;
	}

	if (!state.loaded && !state.loading) {
		if (loadGarageFromCache(isVehicleSpecificPage)) {
			return state.garage;
		}

		loadUserGarage();
	}
	return null;
}

const handleGarageData = {
	getGarageData,
	clearGarageCache,
};

module.exports = {
	handleGarageData,
};
